import { db } from '../firebase'
import { deleteField, doc, getDoc, updateDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../firebase'
import type { SignupFormData } from '../types/form.types'
import type { User, UserWithId } from '../types/user.types'

import { apiSlice } from './apiSlice'
import { setCredentials } from './authSlice'

// Helper type for error responses
interface FirebaseError {
  code?: string
  message: string
}

export const userApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({

    // Add member details to firestore
    completeMemberDetails: builder.mutation<unknown, SignupFormData>({
      async queryFn(data) {
        try {
          const callable = httpsCallable(functions, 'completeMemberDetails')
          const result = await callable(data)

          return { data: result.data }
        } catch (error) {
          const err = error as FirebaseError
          return {
            error: {
              status: err.code || 500,
              data: {
                message: err.message
              }
            },
          }
        }
      },
      invalidatesTags: [{ type: 'User' }]
    }),

    fetchUserData: builder.query<UserWithId, string>({
      async queryFn(id, { dispatch }): Promise<any> {
        try {
          if (!id) {
            throw new Error("Id is not provided")
          }

          const userDoc = await getDoc(doc(db, 'users', id))

          if (userDoc.exists()) {
            const userData = userDoc.data() as User

            const clubsArray = Object.entries(userData.clubs).map(([clubId, club]) => {
              const clubPositions = Object.keys(club.positions || {})

              return {
                clubId,
                clubName: club.clubName,
                positions: clubPositions
              }
            })

            return { data: { ...userData, uid: id, clubs: clubsArray } }
          } else {
            throw new Error('User document not found')
          }
        } catch (error) {
          const err = error as FirebaseError
          return {
            error: {
              status: err.code || 500,
              data: {
                message: err.message
              }
            },
          }
        }
      },
      providesTags: (result, error, id) => [{ type: 'User', id: id }]
    }),

    updateUser: builder.mutation({
      async queryFn(data) {
        try {
          const callable = httpsCallable(functions, 'updateUser')
          const result = await callable(data)

          return { data: result.data }
        } catch (error) {
          const err = error as FirebaseError
          return {
            error: {
              status: err.code || 500,
              data: {
                message: err.message
              }
            },
          }
        }
      },
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        // If the user is an admin, update the usersSummary instead of fetching all users data again
        if (data.role === "admin") {
          const { uid } = data
          await queryFulfilled
          dispatch(
            // @ts-expect-error - RTK Query doesn't know about endpoints from other slices
            apiSlice.util.updateQueryData('fetchUsers', undefined, (draft: any) => {
              if (draft) {
                draft[uid].name = `${data.firstName} ${data.lastName}`
              }
            })
          )
        }
      },
      invalidatesTags: (result: any, error, { uid }: any) => {
        const tags: any[] = [{ type: 'User', id: uid }]

        // Add club tags if clubs were updated
        if (result?.clubsUpdated?.length > 0) {
          result.clubsUpdated.forEach((clubId: string) => {
            tags.push({ type: 'Club', id: clubId })
            tags.push({ type: 'ClubMembers', id: clubId })
          })
        }

        return tags
      }
    }),

    updateEmail: builder.mutation({
      async queryFn(data) {
        try {
          const callable = httpsCallable(functions, 'updateEmail')
          const result = await callable(data)

          return { data: result.data }
        } catch (error) {
          const err = error as FirebaseError
          return {
            error: {
              status: err.code || 500,
              data: {
                message: err.message
              }
            },
          }
        }
      },
      invalidatesTags: [{ type: 'User' }, { type: "Club" }],
    }),

    clearNotifications: builder.mutation({
      async queryFn(uid, _queryApi) {
        // Update the cached data for usersSummary in RTK Query
        _queryApi.dispatch(
          // @ts-expect-error - Optimistic update for cache
          apiSlice.util.updateQueryData('fetchUserData', uid, (draft: any) => {
            if (draft) {
              draft.notifications = 0
            }
          })
        )

        try {
          await updateDoc(doc(db, 'users', uid), {
            notifications: 0
          })

          return { data: 'Notifications cleared' }
        } catch (error) {
          console.log(error)
          const err = error as FirebaseError
          return {
            error: {
              status: err.code || 500,
              data: {
                message: err.message
              }
            },
          }
        }
      }
    }),

    // Delete user notification
    deleteUserNotification: builder.mutation({
      async queryFn({ uid, docId, category }) {
        try {
          if (!uid || !docId || !category) {
            console.log("All data is not provided")
            throw new Error("Correct data is not provided")
          }

          await updateDoc(doc(db, 'users', uid), {
            [`${category}.${docId}`]: deleteField()
          })

          return { data: 'Notification deleted' }
        } catch (error) {
          console.log(error)
          const err = error as FirebaseError
          return {
            error: {
              status: err.code || 500,
              data: { message: err.message }
            }
          }
        }
      },
      async onQueryStarted({ uid, docId, category }, { dispatch, queryFulfilled }) {
        dispatch(
          // @ts-expect-error - Optimistic update for cache
          apiSlice.util.updateQueryData("fetchUserData", uid, (draft: any) => {
            if (draft?.[category]?.[docId]) {
              delete draft[category][docId]
            }
          }),
        )
        try {
          await queryFulfilled
        } catch (error) {
          dispatch(apiSlice.util.invalidateTags(["User"]))
        }
      }
    }),

    // Mark notification as read
    notificationsRead: builder.mutation({
      async queryFn({ invites = [], messages = [] }, { getState }) {
        try {
          const state = getState() as any

          const uid = state.auth.user.uid

          if (!invites.length && !messages.length) {
            throw new Error('No notifications to update')
          }

          const userRef = doc(db, 'users', uid)
          const updates = {}

          invites.forEach(invite => {
            updates[`invites.${invite}.read`] = true
          })

          messages.forEach(message => {
            updates[`messages.${message}.read`] = true
          })

          await updateDoc(userRef, updates)

          return { data: 'Notifications marked as read' }
        } catch (error) {
          const err = error as FirebaseError
          return {
            error: {
              status: err.code || 500,
              data: {
                message: err.message
              }
            },
          }
        }
      },
      async onQueryStarted({ invites, messages, id }, { dispatch, queryFulfilled }) {
        // Optimistic update
        dispatch(
          // @ts-expect-error - Optimistic update for cache
          apiSlice.util.updateQueryData("fetchUserData", id, (draft: any) => {
            if (!draft) return

            // Mark all specified request IDs as read
            invites.forEach((id) => {
              if (draft.invites?.[id]) {
                draft.invites[id].read = true
              }
            })

            // Mark all specified message IDs as read
            messages.forEach((id) => {
              if (draft.messages?.[id]) {
                draft.messages[id].read = true
              }
            })
          })
        )

        try {
          await queryFulfilled
        } catch (error) {
          // Refetch fresh data if update fails
          dispatch(apiSlice.util.invalidateTags(["Notifications"]))
        }
      }
    }),

    // Leave club
    leaveClub: builder.mutation({
      async queryFn(data) {
        try {
          const callable = httpsCallable(functions, 'leaveClub')
          await callable(data)

          return { data: 'Left club successfully' }
        } catch (error) {
          return {
            error: {
              status: error.code || 500,
              data: {
                message: error.message
              }
            }
          }
        }
      },
      invalidatesTags: (result, error, { uid }) => {
        return [{ type: 'User', id: uid }]
      }
    })
  })
})

export const {
  useCompleteMemberDetailsMutation,
  useUpdateUserMutation,
  useUpdateEmailMutation,
  useNotificationsReadMutation,
  useFetchUserDataQuery,
  useClearNotificationsMutation,
  useDeleteUserNotificationMutation,
  useLeaveClubMutation
} = userApiSlice